import { SelectItem, Select, ISelectProps } from '../Select';
import { IBaseSelectProps, OptionType, useSelectOptionHelpers } from './selectHelpers';
import { ReadOnly } from '../ReadOnly';
import { useFieldsDisabled, useFieldsReadonly } from '../Form';
import { Box, BoxProps, SxProps, Theme } from '@mui/material';
import { renderTooltipLabel } from '../Tooltip';
import { ReactNode } from 'react';

export interface ISortedSelectProps<T, V> extends IBaseSelectProps<T, V>, Omit<ISelectProps, 'onChange' | 'value'> {
    nullOptionLabel?: string;
    prompt?: string;
    renderLabel?: (label: string) => ReactNode;
    inputLabelSx?: SxProps<Theme>;
    stacked?: boolean;
    labelBoxSx?: BoxProps['sx'];
    selectContainerSx?: BoxProps['sx'];
    error?: boolean;
    helperText?: string;
    dynamicLabelSize?: boolean;
}

export function SortedSelect<T>({
    nullOptionLabel,
    options,
    prompt,
    label,
    labelId,
    id,
    value,
    name,
    onChange,
    disableSorting,
    sortComparisonFn,
    readonly,
    required,
    testId,
    tooltip,
    disabled,
    renderValue,
    renderLabel,
    inputLabelSx,
    stacked,
    labelBoxSx,
    selectContainerSx,
    error,
    helperText,
    dynamicLabelSize = false,
}: ISortedSelectProps<T, OptionType<T> | null>) {
    const { normalizedOptions, normalizedValue, handleChange } = useSelectOptionHelpers(
        value,
        options,
        onChange,
        disableSorting,
        sortComparisonFn
    );
    const isReadonly = useFieldsReadonly(readonly);
    const isDisabled = useFieldsDisabled(disabled);

    const nullOption =
        typeof nullOptionLabel === 'string' ? (
            <SelectItem
                value={nullOptionLabel}
                sx={{
                    '& > .blank-label': {
                        height: '1em',
                    },
                }}
            >
                {nullOptionLabel.length ? nullOptionLabel : <div className="blank-label" />}
            </SelectItem>
        ) : null;
    const promptItem = prompt ? (
        <SelectItem value={prompt} disabled>
            {prompt}
        </SelectItem>
    ) : null;

    function handleValueChange(newValue: string) {
        if (newValue === prompt || newValue === nullOptionLabel) {
            handleChange(null);
        } else {
            handleChange(newValue);
        }
    }

    if (isReadonly) {
        return <ReadOnly testId={testId} label={label} value={normalizedValue?.label} tooltip={tooltip} />;
    }

    const formatLabel = (text: string) => {
        if (text.includes(' ')) {
            return text.replace(' ', '\n');
        }
        return text;
    };

    // Render label with asterisk on the right
    const renderFormLabel = () => {
        if (!label) {
            return null;
        }

        // Detect long continuous strings (no spaces and longer than typical width)
        const isLongContinuousString = typeof label === 'string' && !label.includes(' ') && label.length > 15;

        const finalLabel = dynamicLabelSize && typeof label === 'string' ? formatLabel(label) : label;

        const resolvedLabelId = labelId ?? (id ? `${id}-label` : undefined);

        // MUI Select puts `id` on the combobox div, not a labelable native control — use a
        // named div + aria-labelledby on the select instead of <label htmlFor>.
        return (
            <Box component="div" id={resolvedLabelId}>
                <Box
                    sx={{
                        width: stacked ? 160 : { xs: '100%', md: 135 },
                        marginRight: stacked ? '' : { xs: 0, md: '25px' },
                        marginBottom: stacked ? 0 : { xs: 0.5, md: 0 },
                        display: 'flex',
                        alignItems: 'center',
                        // For long continuous strings, allow text wrapping within the fixed width
                        wordBreak: isLongContinuousString ? 'break-word' : 'normal',
                        overflowWrap: 'break-word',
                        lineHeight: isLongContinuousString ? 1.2 : 'normal',
                        hyphens: isLongContinuousString ? 'auto' : 'none',
                        ...(dynamicLabelSize && {
                            '& > span': {
                                whiteSpace: 'pre-line',
                            },
                        }),
                        ...labelBoxSx,
                    }}
                >
                    {renderTooltipLabel({ tooltip, label: finalLabel })}
                    {required && (
                        <Box
                            component="span"
                            sx={{
                                marginLeft: '4px', // Add spacing between label and asterisk
                            }}
                        >
                            *
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Box
            sx={
                stacked
                    ? { display: 'flex', flexDirection: 'column', width: '100%' }
                    : {
                          display: 'flex',
                          alignItems: { xs: 'stretch', md: 'center' },
                          flexDirection: { xs: 'column', md: 'row' },
                          width: '100%',
                      }
            }
        >
            {renderFormLabel()}
            <Select
                testId={testId}
                disabled={isDisabled}
                id={id}
                labelId={labelId ?? (id ? `${id}-label` : undefined)}
                aria-label={typeof label === 'string' ? label : undefined}
                value={normalizedValue?.label ?? nullOptionLabel ?? prompt ?? ''}
                name={name}
                onChange={(ev) => handleValueChange(ev.target.value as string)}
                required={required}
                renderValue={renderValue}
                inputLabelSx={inputLabelSx}
                selectContainerSx={selectContainerSx}
                error={error}
                helperText={helperText}
            >
                {promptItem}
                {nullOption}
                {normalizedOptions.map((option) => (
                    <SelectItem data-testid={`${testId}-${option.label}`} value={option.label} key={option.label}>
                        {renderLabel ? renderLabel(option.label) : option.label}
                    </SelectItem>
                ))}
            </Select>
        </Box>
    );
}
